import { useCallback, useEffect, useState } from 'react';
import { HardDrive, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { apiAdminBackups } from '../api/client.js';

const card = {
  background: 'var(--card-bg)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-md, 12px)',
  padding: 16,
};

function fmtBytes(n) {
  if (!n || n <= 0) return '—';
  const u = ['o', 'Ko', 'Mo', 'Go'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

function fmtDuration(s) {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m${r ? ` ${r}s` : ''}`;
}

function fmtAge(s) {
  if (s == null) return 'jamais';
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
}

function fmtDateTime(epochS) {
  if (!epochS) return '—';
  return new Date(epochS * 1000).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function BackupsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiAdminBackups.list();
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div style={{ padding: 20 }}>Chargement…</div>;
  if (error) return <div style={{ padding: 20, color: 'var(--danger)' }}>Erreur : {error}</div>;

  const summary = data?.summary ?? [];
  const runs = data?.runs ?? [];

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
          <HardDrive size={22} /> Sauvegardes
        </h1>
        <button
          type="button"
          onClick={load}
          title="Rafraîchir"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'var(--glass)',
            border: '1px solid var(--glass-border)',
            borderRadius: 999,
            padding: '6px 14px',
            cursor: 'pointer',
            color: 'var(--text)',
          }}
        >
          <RefreshCw size={15} /> Rafraîchir
        </button>
      </div>

      {/* Résumé par environnement */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 16,
          marginBottom: 24,
        }}
      >
        {summary.length === 0 && (
          <div style={{ ...card, color: 'var(--text-muted)' }}>
            Aucune sauvegarde enregistrée sur ce serveur.
          </div>
        )}
        {summary.map((s) => (
          <div key={s.env} style={card}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <strong style={{ textTransform: 'capitalize' }}>{s.env}</strong>
              {s.stale ? (
                <span
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--danger)' }}
                  title="Aucun backup réussi depuis plus de 26 h"
                >
                  <AlertTriangle size={16} /> En retard
                </span>
              ) : (
                <span
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--success)' }}
                >
                  <CheckCircle2 size={16} /> À jour
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>
              <div>
                Dernier run :{' '}
                {s.lastRunOk ? (
                  <span style={{ color: 'var(--success)' }}>OK</span>
                ) : (
                  <span style={{ color: 'var(--danger)' }}>échec</span>
                )}{' '}
                ({fmtDateTime(s.lastRunAt)})
              </div>
              <div>Dernier succès : {fmtAge(s.lastSuccessAgeSeconds)}</div>
              <div style={{ opacity: 0.7 }}>hôte : {s.host}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Historique des runs */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 14px' }}>Statut</th>
              <th style={{ padding: '10px 14px' }}>Environnement</th>
              <th style={{ padding: '10px 14px' }}>Date</th>
              <th style={{ padding: '10px 14px' }}>Durée</th>
              <th style={{ padding: '10px 14px' }}>Taille</th>
              <th style={{ padding: '10px 14px' }}>Schemas</th>
              <th style={{ padding: '10px 14px' }}>Détail</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 16, color: 'var(--text-muted)' }}>
                  Aucun run enregistré.
                </td>
              </tr>
            )}
            {runs.map((r, i) => (
              <tr key={`${r.ts}-${i}`} style={{ borderTop: '1px solid var(--glass-border)' }}>
                <td style={{ padding: '10px 14px' }}>
                  {r.ok ? (
                    <CheckCircle2 size={18} style={{ color: 'var(--success)' }} title="Réussi" />
                  ) : (
                    <XCircle size={18} style={{ color: 'var(--danger)' }} title="Échec" />
                  )}
                </td>
                <td style={{ padding: '10px 14px', textTransform: 'capitalize' }}>{r.env}</td>
                <td style={{ padding: '10px 14px' }}>{fmtDateTime(r.finished || r.ts)}</td>
                <td style={{ padding: '10px 14px' }}>{fmtDuration(r.duration_s)}</td>
                <td style={{ padding: '10px 14px' }}>{fmtBytes(r.archive_bytes)}</td>
                <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>
                  {(r.schemas ?? []).length}
                </td>
                <td style={{ padding: '10px 14px', color: 'var(--text-muted)', maxWidth: 280 }}>
                  {r.ok
                    ? (r.schemas ?? []).map((s) => s.db).join(', ')
                    : <span style={{ color: 'var(--danger)' }}>{r.error || 'erreur'}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
